import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// Merge Tailwind classes
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format currency (BRL)
export function formatCurrency(valueInCents: number | string): string {
  const value = typeof valueInCents === 'string' ? parseInt(valueInCents, 10) : valueInCents;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
}

// Format compact currency for large values (avoids overflow in cards)
export function formatCompactCurrency(valueInCents: number | string): string {
  const raw = typeof valueInCents === 'string' ? parseInt(valueInCents, 10) : valueInCents;
  const value = raw / 100;
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs >= 1_000_000_000) {
    return `${sign}R$ ${(abs / 1_000_000_000).toFixed(1).replace('.', ',')}B`;
  }
  if (abs >= 1_000_000) {
    return `${sign}R$ ${(abs / 1_000_000).toFixed(1).replace('.', ',')}M`;
  }
  if (abs >= 100_000) {
    return `${sign}R$ ${(abs / 1_000).toFixed(0).replace('.', ',')}K`;
  }
  return formatCurrency(raw);
}

// Format date
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  }).format(d);
}

// Format datetime
export function formatDateTime(date: string | Date): string {
  return formatDate(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Format CPF/CNPJ
export function formatCpfCnpj(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 11) {
    // CPF: 000.000.000-00
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  // CNPJ: 00.000.000/0000-00
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

// Format phone
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
}

// Mask Pix key for display
export function maskPixKey(key: string, type: string): string {
  if (!key) return '';
  
  switch (type) {
    case 'CPF':
      return `***.***.${key.slice(-5, -2)}-**`;
    case 'CNPJ':
      return `**.***.***/${key.slice(-6, -2)}-**`;
    case 'EMAIL':
      const [user, domain] = key.split('@');
      return `${user.slice(0, 2)}***@${domain}`;
    case 'PHONE':
      return `(**) *****-${key.slice(-4)}`;
    case 'RANDOM':
      return `${key.slice(0, 8)}...${key.slice(-4)}`;
    default:
      return key;
  }
}

// Generate initials from name
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Truncate text
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return `${text.slice(0, length)}...`;
}

// Sleep utility
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Copy to clipboard
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// Download file
export function downloadFile(data: Blob, filename: string): void {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// Status color mapping
export function getStatusColor(status: string): string {
  const statusMap: Record<string, string> = {
    ACTIVE: 'success',
    COMPLETED: 'success',
    APPROVED: 'success',
    PENDING: 'warning',
    PROCESSING: 'info',
    IN_REVIEW: 'info',
    SCHEDULED: 'info',
    FAILED: 'error',
    CANCELLED: 'error',
    REJECTED: 'error',
    SUSPENDED: 'error',
    INACTIVE: 'error',
    EXPIRED: 'error',
  };
  return statusMap[status] || 'info';
}

// Transaction type labels
export function getTransactionTypeLabel(type: string): string {
  const typeMap: Record<string, string> = {
    PIX_RECEIVE: 'Pix Recebido',
    PIX_SEND: 'Pix Enviado',
    TRANSFER_INTERNAL: 'Transferência Interna',
    BOLETO: 'Boleto',
    CARD_PAYMENT: 'Cartão',
    REFUND: 'Estorno',
    SPLIT_DISTRIBUTION: 'Split',
    WITHDRAWAL: 'Saque',
  };
  return typeMap[type] || type;
}

// Account type labels
export function getAccountTypeLabel(type: string): string {
  const typeMap: Record<string, string> = {
    BOLSAO: 'Conta Master',
    VIRTUAL: 'Conta Pagamento',
    REAL: 'Conta Real',
  };
  return typeMap[type] || type;
}

// User type labels
export function getUserTypeLabel(type: string): string {
  const typeMap: Record<string, string> = {
    ADMIN: 'Administrador',
    EMPLOYEE: 'Funcionário',
    CUSTOMER: 'Cliente',
    SUPPLIER: 'Fornecedor',
  };
  return typeMap[type] || type;
}

// Format document (CPF/CNPJ) - alias for formatCpfCnpj
export function formatDocument(value: string): string {
  return formatCpfCnpj(value);
}
